import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { Routes, HiddenHeaderPathnames } from "../../services/constants";
import { AccountMenuButton, AccountMenuItem } from "../account-menu-button/AccountMenuButton";
import { Cart } from "../cart/Cart";
import IconMenuBurger from "../icon/IconMenuBurger";
import IconLogoSuccessFactory from "../icon/IconLogoSuccessFactory";
import { Menu } from "../menu/Menu";
import { MobileMenu } from "../mobile-menu/MobileMenu";
import { ProductSelectorProduct } from "../products-selector/ProductsSelector";
import { IconSignIn } from "../icon/IconSignIn";
import { PaymentMethodsSource } from "../../views/account-view/payment-methods-view/payment-methods-section/PaymentMethodsSection";
import { PaymentSourceScrCredit, PaymentSourceShopCredit, usePaymentMethodsViewQuery } from "../../graphql/schema";

import styles from "./header.module.scss";

export enum HeaderGutter {
  MEDIUM = "MEDIUM",
}

export interface HeaderProps {
  isAuthenticated: boolean;
  isViewerActive: boolean;
  hasAccessToWebOffice: boolean;
  gutter?: keyof typeof HeaderGutter | number;
  primary?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  navigateToUrl?: string | false;
  products: ProductSelectorProduct[];
  accountMenuItems: AccountMenuItem[];
  className?: string;
  affiliateId?: number;
}

export const Header: React.FC<HeaderProps> = ({
  isAuthenticated,
  isViewerActive,
  hasAccessToWebOffice,
  gutter,
  secondary,
  tertiary,
  primary = !secondary && !tertiary,
  navigateToUrl,
  products,
  accountMenuItems = [],
  className,
  affiliateId,
}) => {
  const { pathname } = useLocation();
  const query = usePaymentMethodsViewQuery();

  const savedPaymentSources = query.data?.me.paymentSources;
  //   const d2tBalanceInfo = query.data?.me?.meD2TInfo?.d2tBalance ?? "0";

  const topUp = getShopCreditInfo(savedPaymentSources);
  const scrInfo = getScrCreditInfo(savedPaymentSources);

  const prevBalanceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!topUp?.available) return;

    const current = parseFloat(topUp.available);

    // Skip first load
    if (prevBalanceRef.current === null) {
      prevBalanceRef.current = current;
      return;
    }

    const prev = prevBalanceRef.current;
    const diff = current - prev;

    if (diff !== 0) {
      toast(
        <div style={{ display: "flex", gap: 6 }}>
          <span>Balance updated:</span>
          <span
            style={{
              color: diff > 0 ? "#16a34a" : "#dc2626",
              fontWeight: 600,
            }}
          >
            {diff > 0 ? `+${diff}` : diff} USDS
          </span>
        </div>,
        {
          autoClose: false,
          closeOnClick: true,
        },
      );

      prevBalanceRef.current = current;
    }
  }, [topUp?.available]);

  // check if header should be hidden
  const hiddenHeader = Object.values(HiddenHeaderPathnames).find((url) => pathname.startsWith(url));

  // control mobile menu open state
  const [openMenu, setOpenMenu] = useState(false);

  const [t] = useTranslation();

  if (hiddenHeader) {
    return null;
  }

  const primaryContent = () => (
    <>
      <div className={styles["header-wrap"]}>
        <div className={styles["nav-link-wrap"]}>
          <NavLink exact to="/" className={styles["header-link"]} activeClassName={styles["active-link"]}>
            {t("Home")}
          </NavLink>
          {isAuthenticated ? (
            <NavLink
              exact
              to="/account/products"
              className={styles["header-link"]}
              activeClassName={styles["active-link"]}
            >
              {t("Products")}
            </NavLink>
          ) : (
            <NavLink exact to="/awentures" className={styles["header-link"]} activeClassName={styles["active-link"]}>
              {t("Products")}
            </NavLink>
          )}
          {/* <NavLink to={Routes.ABOUT} className={styles["header-link"]} activeClassName={styles["active-link"]}>
          {t("About")}
        </NavLink> */}
          {/* {products.length > 0 && <ProductsSelector triggerClassName={styles["header-link"]} products={products} />} */}
        </div>
        <Link to="/">
          <IconLogoSuccessFactory className={styles.logo} />
        </Link>
        <div className={styles["header-icons"]}>
          {/* {!isAuthenticated && (
            <Link
              to={affiliateId ? `/registration/${affiliateId}` : Routes.REQUEST_INVITE}
              className={styles["sign-up"]}
            >
              {t("Sign up")}
              <IconAddUser className={styles["sign-up-icon"]} />
            </Link>
          )} */}

          {isAuthenticated ? (
            <>
              <div className={styles["balances"]}>
                <div className={styles["balance-item"]}>
                  <span className={styles["balance-value"]}>{topUp?.available}</span>
                  <span className={styles["balance-label"]}>USDS</span>
                </div>
                <div className={styles["balance-item"]}>
                  <span className={styles["balance-value"]}>{scrInfo?.available}</span>
                  <span className={styles["balance-label"]}>SCR</span>
                </div>
                {/* <div className={styles["balance-item"]}>
                  <span className={styles["balance-value"]}>{d2tBalanceInfo}</span>
                  <span className={styles["balance-label"]}>D2T</span>
                </div> */}
              </div>
            </>
          ) : null}
          <div className={styles["header-icon-holder"]}>
            {isAuthenticated ? (
              <AccountMenuButton
                hasAccessToWebOffice={hasAccessToWebOffice}
                isActive={isViewerActive}
                accountMenuItems={accountMenuItems}
              />
            ) : (
              <Link to={Routes.LOGIN} className={styles.login}>
                {t("Login").toUpperCase()}
                <IconSignIn className={styles["login-icon"]} />
              </Link>
            )}
            {/* <LanguageSelect /> */}
            <Cart />
          </div>
        </div>
        <div className={styles["burger-wrap"]}>
          <div onClick={() => setOpenMenu(!openMenu)}>
            <IconMenuBurger />
          </div>
          <Menu open={openMenu} setOpen={setOpenMenu}>
            <MobileMenu
              hasAccessToWebOffice={hasAccessToWebOffice}
              setOpen={setOpenMenu}
              isAuthenticated={isAuthenticated}
              products={products}
              accountMenuItems={accountMenuItems}
            />
          </Menu>
        </div>
      </div>
    </>
  );

  const secondaryContent = () => (
    <>
      <IconLogoSuccessFactory logoFill="#92929d" textFill="#92929d" className={styles["logo--secondary"]} />
      {navigateToUrl && (
        <Link to={navigateToUrl} className={styles["logout-link"]}>
          {t("Logout")}
        </Link>
      )}
    </>
  );

  const tertiaryContent = () => (
    <Link to="/">
      <IconLogoSuccessFactory className={styles["logo--secondary"]} />
    </Link>
  );

  // heading types
  const headerTypes = () =>
    classNames({
      [styles["header--primary"]]: primary,
      [styles["header--tertiary"]]: tertiary,
    });

  // heading gutters
  const gutters = () =>
    classNames({
      [styles["header--gutter-medium"]]: gutter === "MEDIUM",
    });

  // check if gutter value is a number
  const customGutter = typeof gutter === "number" ? gutter : undefined;

  return (
    <div
      style={{ marginBottom: customGutter }}
      className={classNames(
        styles.header,
        headerTypes(),
        gutters(),
        {
          [styles["header--has-navigation"]]: navigateToUrl,
        },
        className,
      )}
    >
      {primary && primaryContent()}
      {secondary && secondaryContent()}
      {tertiary && tertiaryContent()}

      <Helmet>
        <meta charSet="utf-8" />
        <link rel="canonical" href={`${process.env.REACT_APP_HOST}${pathname}`} />
        <meta property="og:url" content={pathname} />
      </Helmet>
    </div>
  );
};

function getShopCreditInfo(sources?: PaymentMethodsSource[] | null) {
  if (!sources) {
    return null;
  }

  const topUpSource = sources.filter((e) => e.info && e.info.__typename === "PaymentSourceShopCredit")[0];

  return topUpSource.info as PaymentSourceShopCredit;
}
function getScrCreditInfo(sources?: PaymentMethodsSource[] | null) {
  if (!sources) {
    return null;
  }

  const topUpSource = sources.filter((e) => e.info && e.info.__typename === "PaymentSourceScrCredit")[0];

  return topUpSource.info as PaymentSourceScrCredit;
}
